import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Award, Users, Shield, Target, Hammer, Building, Eye, Star, Anvil, Phone } from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import CountUp from '../components/CountUp';
import Logo from '../components/Logo';
import AnimatedCounter from '../components/AnimatedCounter';

const Home = () => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    document.title = "Ceballos Total Framing - Arizona's Premier Structural Specialists | Paradise Valley & Scottsdale Elite";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Arizona\'s most distinguished structural framing specialists. Serving Paradise Valley, Arcadia, and Scottsdale\'s finest custom homes with uncompromising craftsmanship. CR-7 Licensed, 25+ years of architectural excellence.');
    }
  }, []);

  return (
    <div className="overflow-hidden bg-gray-900">
      {/* Architectural Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        {/* Video Background */}
        <div className="absolute inset-0 overflow-hidden" style={{ top: '18rem' }}>
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="w-full h-full object-cover transition-opacity duration-1000"
            style={{ 
              filter: 'brightness(0.7) contrast(1.1)',
              opacity: videoLoaded ? 1 : 0
            }}
            onLoadedData={() => setVideoLoaded(true)}
            onError={() => setVideoError(true)}
            onEnded={(e) => e.target.currentTime = 0}
          >
            <source src="https://snt8981skak946ql.public.blob.vercel-storage.com/Video2.mp4" type="video/mp4" />
            {/* Fallback background if video fails to load */}
            <div className="absolute inset-0 bg-gray-900"></div>
          </video>
          
          {/* Fallback background while video loads or on error */}
          {(!videoLoaded || videoError) && (
            <div className="absolute inset-0 bg-gray-900"></div>
          )}
          
          {/* Light overlay for better text readability */}
          <div className="absolute inset-0 bg-black/20"></div>
          

          
          {/* Video loading indicator */}
          {!videoLoaded && !videoError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin"></div>
            </div>
          )}
        </div>
        
        <div className="relative z-20 text-center text-white px-6 max-w-8xl mx-auto pt-72">
          <AnimatedSection delay={200}>
            <div className="mb-16">
              <Logo size="large" />
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={400}>
            <h1 className="mb-12 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              <span className="block text-4xl md:text-6xl font-light text-gray-300 mb-4 tracking-wide">Arizona's Premier</span>
              <span className="block text-5xl md:text-8xl font-normal bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 bg-clip-text text-transparent mb-4">
                Structural Artisans
              </span>
              <span className="block text-2xl md:text-4xl font-light text-gray-400 tracking-wider uppercase">
                Since 2018
              </span>
            </h1>
          </AnimatedSection>
          
          <AnimatedSection delay={600}>
            <div className="max-w-4xl mx-auto mb-16">
              <p className="text-xl md:text-2xl mb-8 font-light leading-relaxed text-gray-300" style={{ fontFamily: 'Inter, sans-serif' }}>
                Where architectural vision meets precision framing
              </p>
              <div className="h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent mb-8"></div>
              <p className="text-lg font-light text-gray-400 leading-relaxed tracking-wide" style={{ fontFamily: 'Inter, sans-serif' }}>
                The discerning choice for Arizona's most distinguished builders
              </p>
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={800}>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-20">
              <Link 
                to="/contact"
                className="group relative overflow-hidden bg-gradient-to-r from-amber-500 to-amber-600 text-white px-12 py-4 transition-all duration-500 transform hover:scale-105 shadow-2xl hover:shadow-amber-500/25 text-lg font-medium tracking-wide"
                style={{ borderRadius: '8px', fontFamily: 'Inter, sans-serif' }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-amber-600 to-amber-700 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" style={{ borderRadius: '8px' }}></div>
                <span className="relative z-10 flex items-center">
                  Begin Your Build
                  <ArrowRight className="ml-3 h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" />
                </span>
              </Link>
              
              <Link 
                to="/portfolio"
                className="group border border-amber-400/60 text-white px-12 py-4 hover:bg-amber-400/10 transition-all duration-500 backdrop-blur-md shadow-xl text-lg font-medium tracking-wide"
                style={{ borderRadius: '8px', fontFamily: 'Inter, sans-serif' }}
              >
                <span className="flex items-center">
                  View Portfolio
                  <Eye className="ml-3 h-5 w-5 transform group-hover:scale-110 transition-transform duration-300" />
                </span>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Market Position Section - Architectural */}
      <section className="py-32 bg-gray-800 relative overflow-hidden">
        {/* Architectural Divider */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        
        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative">
          
          <AnimatedSection delay={200}>
            <div className="text-center mb-24">
              <div className="inline-block mb-8">
              </div>
              <h2 className="text-4xl md:text-6xl font-light mb-12 leading-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                Where <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Excellence</span>
                <br />Defines Every Detail
              </h2>
              <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-16 font-light leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                Serving Arizona's most exclusive communities with precision framing that transcends conventional boundaries
              </p>
            </div>
          </AnimatedSection>
          
          {/* Elite Service Areas */}
          <AnimatedSection delay={400}>
            <div className="flex flex-wrap justify-center gap-8 mb-20">
              {[
                { name: 'Paradise Valley', desc: 'Camelback mountain estates' },
                { name: 'North Scottsdale', desc: 'Contemporary masterpieces' },
                { name: 'Arcadia', desc: 'Historic elegance refined' }
              ].map((area, idx) => (
                <div key={idx} className="group relative">
                  <div className="bg-gray-700/50 backdrop-blur-sm px-8 py-6 border border-amber-400/20 hover:border-amber-400/40 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2" style={{ borderRadius: '8px' }}>
                    <div className="flex items-center space-x-4">
                      <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                      <div>
                        <div className="font-medium text-xl text-white" style={{ fontFamily: 'Playfair Display, serif' }}>{area.name}</div>
                        <div className="text-sm text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>{area.desc}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </AnimatedSection>

          {/* Premium Investment Metrics */}
          <AnimatedSection delay={600}>
            <div className="bg-gray-900 p-16 border border-amber-400/20 shadow-2xl relative" style={{ borderRadius: '12px' }}>
              <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                <div className="text-center group">
                  <div className="relative mb-8">
                    <div className="text-4xl md:text-6xl font-light bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent" style={{ fontFamily: 'Playfair Display, serif' }}>
                      <AnimatedCounter end={500} />K - 1.<AnimatedCounter end={5} />M
                    </div>
                  </div>
                  <div className="text-xl font-light text-gray-300 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>Average Project Value</div>
                  <div className="text-sm text-gray-400 leading-relaxed font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Premium custom homes and luxury commercial developments across Arizona's most exclusive markets</div>
                </div>
                
                <div className="text-center group">
                  <div className="relative mb-8">
                    <div className="text-4xl md:text-6xl font-light bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent" style={{ fontFamily: 'Playfair Display, serif' }}>
                      <AnimatedCounter end={25} />+
                    </div>
                  </div>
                  <div className="text-xl font-light text-gray-300 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>Years of Mastery</div>
                  <div className="text-sm text-gray-400 leading-relaxed font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Unbroken legacy of structural excellence serving Arizona's most elite clientele</div>
                </div>
                
                <div className="text-center group">
                  <div className="relative mb-8">
                    <div className="text-4xl md:text-6xl font-light bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent" style={{ fontFamily: 'Playfair Display, serif' }}>
                      <AnimatedCounter end={12} />M+
                    </div>
                  </div>
                  <div className="text-xl font-light text-gray-300 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>Structural Investment</div>
                  <div className="text-sm text-gray-400 leading-relaxed font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Total project values completed with zero structural failures or warranty claims</div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Dual Mastery Section - Architectural */}
      <section className="py-32 bg-gray-900 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        
        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative">
          
          <AnimatedSection delay={200}>
            <div className="text-center mb-24">
              <div className="inline-block mb-8">
              </div>
              <h2 className="text-4xl md:text-6xl font-light mb-12 leading-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Wood</span> 
                <span className="mx-8 text-gray-400 text-3xl">+</span>
                <span className="font-normal bg-gradient-to-r from-gray-300 to-gray-400 bg-clip-text text-transparent">Steel</span>
              </h2>
              <p className="text-xl text-gray-300 max-w-4xl mx-auto font-light leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                Where traditional wood craftsmanship converges with advanced steel engineering to create Arizona's most structurally sophisticated homes
              </p>
            </div>
          </AnimatedSection>
          
          <div className="space-y-24 mb-24">
            {/* Wood Framing - Premium - Image Left, Content Right */}
            <AnimatedSection delay={400}>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-16 items-center">
                {/* Project Image */}
                <div className="relative group">
                  <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                    <img 
                      src="/Peoria_MalapaiBuilders2.jpg" 
                      alt="Premium Wood Framing Project - Peoria Malapai Builders"
                      className="w-full h-96 object-cover transform group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent"></div>

                  </div>
                </div>

                {/* Content */}
                <div className="group relative">
                  <div className="bg-gray-800 p-12 border border-amber-400/20 hover:border-amber-400/40 shadow-2xl transition-all duration-500 h-full flex flex-col" style={{ borderRadius: '12px' }}>
                    <div className="flex items-center mb-8">
                      <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center mr-6" style={{ borderRadius: '8px' }}>
                        <Hammer className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-2xl font-medium text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Premium Wood Framing</h3>
                    </div>
                    
                    <p className="text-gray-300 mb-8 leading-relaxed font-light" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Premium materials sourced from trusted suppliers and precision craftsmanship techniques ensure superior structural integrity for demanding applications.
                    </p>
                    
                    <ul className="space-y-4 text-gray-400 flex-grow" style={{ fontFamily: 'Inter, sans-serif' }}>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-amber-400 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="font-light">All premium structural lumber grades</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-amber-400 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="font-light">Professional grade structural connections and fasteners</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-amber-400 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="font-light">Engineered load-bearing calculations and design</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-amber-400 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="font-light">Superior structural stability and longevity</span>
                      </li>
                    </ul>

                    <div className="mt-auto pt-8">
                      <Link 
                        to="/services#wood-steel-mastery"
                        className="inline-flex items-center text-amber-400 hover:text-amber-300 transition-colors duration-300 font-medium"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        Explore Wood Services
                        <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform duration-300" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Steel Framing - Premium - Content Left, Image Right */}
            <AnimatedSection delay={600}>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-16 items-center">
                {/* Content */}
                <div className="group relative order-2 xl:order-1">
                  <div className="bg-gray-800 p-12 border border-gray-400/20 hover:border-gray-400/40 shadow-2xl transition-all duration-500 h-full flex flex-col" style={{ borderRadius: '12px' }}>
                    <div className="flex items-center mb-8">
                      <div className="w-12 h-12 bg-gradient-to-r from-gray-500 to-gray-600 flex items-center justify-center mr-6" style={{ borderRadius: '8px' }}>
                        <Anvil className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-2xl font-medium text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Advanced Structural Steel</h3>
                    </div>
                    
                    <p className="text-gray-300 mb-8 leading-relaxed font-light" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Premium steel sourced from high-quality Arizona suppliers and expert installation for maximum structural performance in even the most challenging designs.
                    </p>
                    
                    <ul className="space-y-4 text-gray-400 flex-grow" style={{ fontFamily: 'Inter, sans-serif' }}>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="font-light">Complete range of structural steel framing systems</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="font-light">Custom welded connections and reinforcements</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="font-light">Long-span capabilities for open concepts</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="font-light">Precise measurements and perfect alignment</span>
                      </li>
                    </ul>

                    <div className="mt-auto pt-8">
                      <Link 
                        to="/services#wood-steel-mastery"
                        className="inline-flex items-center text-gray-400 hover:text-gray-300 transition-colors duration-300 font-medium"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        Explore Steel Services
                        <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform duration-300" />
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Project Image */}
                <div className="relative group order-1 xl:order-2">
                  <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                    <img 
                      src="/Wickenburg_SpryArchitecture1.jpg" 
                      alt="Advanced Structural Steel Project - Wickenburg Spry Architecture"
                      className="w-full h-96 object-cover transform group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent"></div>

                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Social Proof Section - Architectural */}
      <section className="py-32 bg-gray-800 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        
        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative">
          
          <AnimatedSection delay={200}>
            <div className="text-center mb-20">
              <div className="inline-block mb-8">
              </div>
              <h2 className="text-4xl md:text-6xl font-light mb-12 leading-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                Trusted by Arizona's <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Elite</span>
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <AnimatedSection delay={400}>
              <div className="text-center">
                <div className="text-5xl font-light bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <AnimatedCounter end={25} />+
                </div>
                <div className="text-xl font-light text-gray-300 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>Projects Completed</div>
                <div className="text-sm text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Luxury homes across Arizona's most exclusive communities</div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={600}>
              <div className="text-center">
                <div className="text-5xl font-light bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  100%
                </div>
                <div className="text-xl font-light text-gray-300 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>Client Satisfaction</div>
                <div className="text-sm text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Zero structural failures or warranty claims</div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={800}>
              <div className="text-center">
                <div className="text-5xl font-light bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  CR-7
                </div>
                <div className="text-xl font-light text-gray-300 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>Licensed & Insured</div>
                <div className="text-sm text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Fully bonded with comprehensive coverage</div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Contact CTA Section - Architectural */}
      <section className="py-32 bg-gray-900 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        
        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative">
          
          <AnimatedSection delay={200}>
            <div className="text-center">
              <h2 className="text-4xl md:text-6xl font-light mb-8 leading-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                Ready to Begin Your <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Legacy</span>?
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12 font-light leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                Join Arizona's most distinguished builders who trust Ceballos Total Framing for their most important structural projects.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <Link 
                  to="/contact"
                  className="group relative overflow-hidden bg-gradient-to-r from-amber-500 to-amber-600 text-white px-12 py-4 transition-all duration-500 transform hover:scale-105 shadow-2xl hover:shadow-amber-500/25 text-lg font-medium tracking-wide"
                  style={{ borderRadius: '8px', fontFamily: 'Inter, sans-serif' }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-600 to-amber-700 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" style={{ borderRadius: '8px' }}></div>
                  <span className="relative z-10 flex items-center">
                    Start Your Project
                    <ArrowRight className="ml-3 h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </Link>
                
                <a 
                  href="tel:602-448-1723"
                  className="group border border-amber-400/60 text-white px-12 py-4 hover:bg-amber-400/10 transition-all duration-500 backdrop-blur-md shadow-xl text-lg font-medium tracking-wide"
                  style={{ borderRadius: '8px', fontFamily: 'Inter, sans-serif' }}
                >
                  <span className="flex items-center">
                    Call Now
                    <Phone className="ml-3 h-5 w-5 transform group-hover:scale-110 transition-transform duration-300" />
                  </span>
                </a>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Home;